import Stencil from "../stencil";
import Extension from "../base/extension";

/**
 * Collection page core functionality
 * @class
 * @constructor
 * @public
 */
class Collection extends Extension {
  /**
   * @type {HTMLElement}
   */
  container;

  /**
   * ID of the results page section, so we know what section to request via fetch
   * @type {String}
   */
  sectionId;
  
  /**
   * @type {HTMLElement}
   */
  grid;
  
  /**
   * Data injected by collection-grid.liquid
   * @type {String}
   */
  endlessMode;

  /**
   * @type {HTMLElement}
   */
  loadingOverlay;

  constructor(name) {
    super(name);
  }

  /**
   * Initialisation
   */
  init() {
    if(!Stencil.isCollectionPage()) return;

    this.container = document.querySelector("#products-container");
    if(!this.container){
      console.error(this.logPrefix, "No container found");
      return;
    }

    this.grid = this.container.querySelector("#results-grid");
    if(!this.grid){
      console.warn(this.logPrefix, "No result grid found");
    }

    this.sectionId = this.container.dataset.sectionId;
    this.loadingOverlay = this.container.querySelector('.loader');

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for 'load more' clicks, trigger the load. We need to listen on the
    // container, rather than directly on the button, as the button is
    // re-rendered / dynamically injected each time a filter is applied.
    this.container.addEventListener('click', function(event) {
      if(event.target.closest("a")?.classList.contains("load-more")){
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onLoadMoreClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
      };
    }.bind(this));

    // Setup a waypoint for when reaching the end of the products grid, when
    // pagination is endless (on scroll)
    if(this.endlessMode === "scroll"){
      new Waypoint({
        element: this.container,
        handler: function(direction) {
          if(direction === "down"){
            const loadMoreButton = this.container.querySelector(".load-more");
            if(loadMoreButton){
              loadMoreButton.click();
            }
          }
        }.bind(this),
        offset: 'bottom-in-view'
      })
    }
  }

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage(searchParams, event, updateURLHash = true) {
    this.loadingOverlay.classList.remove('d-none');
    console.log('Rendering page', searchParams);
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    this.renderSectionFromFetch(url);

    const params = new URLSearchParams(searchParams);
    params.delete('page');

    if (updateURLHash) {
      this.updateURLHash(params.toString());
    }
  }

  /**
   * Fetch the section and re-render
   * 
   * @param {String} url URL to fetch and render 
   */
  renderSectionFromFetch(url) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        this.renderNewCards(html);
        this.renderPagination(html);
        this.loadingOverlay.classList.add('d-none');
        Waypoint.refreshAll();

        var event = new CustomEvent("collection:updated");
        document.dispatchEvent(event);

        // Alert SWYM so it can instantiate wishlist buttons
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
      });
  }

  /**
   * Extract and append the new product cards' HTML
   * 
   * @param {String} html The HTML returned from fetch 
   */
  renderNewCards(html) {
    // Extract the new product cards from the returned HTML
    const newCards = new DOMParser().parseFromString(html, 'text/html').querySelectorAll("[data-page]");

    // Append the new page of cards
    Array.from(newCards).forEach(function(element) {
      const fragment = document.createDocumentFragment();
      fragment.appendChild(element);
      this.grid.appendChild(fragment);
    }.bind(this));
  }

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html The HTML returned from fetch
   */
  renderPagination(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  }

  /**
   * Start the process of loading in new product cards
   * 
   * @param {Event} event The initiating event
   */
  onLoadMoreClick(event) {
    event.preventDefault();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url, event);
  }

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }
}

export default Collection;